import React, { useState, useEffect, useRef, useMemo } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import {
  dedupeCenters,
  optionMatchesAnyCenterId,
  resolveCanonicalCenterId,
} from '../../utils/centerDedupe';
import {
  FaUserTimes,
  FaUsers,
  FaTrash,
  FaSearch,
  FaDownload,
  FaUserPlus,
  FaTimes,
  FaFilter,
  FaBuilding,
  FaInfoCircle,
  FaMoneyBillWave,
  FaIdCard,
  FaCalendarAlt,
  FaUserTag,
  FaEdit,
  FaChevronDown,
  FaCheck,
  FaUpload
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import styles from './WorkersModal.module.css';
import * as XLSX from 'xlsx';
import EmployeeFileUploadModal from './EmployeeFileUploadModal';

const ROLE_OPTIONS = [
  { id: 'backup-personeli', name: 'Backup Personeli' },
  { id: 'cra', name: 'CRA' },
  { id: 'calisma-baslatma', name: 'Çalışma Başlatma Sorumlusu' },
  { id: 'egitim-sorumlusu', name: 'Eğitim Sorumlusu' },
  { id: 'finans-uzmani', name: 'Finans Uzmanı' },
  { id: 'ik-uzmani', name: 'IK Uzmanı' },
  { id: 'it-uzmani', name: 'IT Uzmanı' },
  { id: 'is-gelistirme', name: 'İş Geliştirme Uzmanı' },
  { id: 'kalite-muduru', name: 'Kalite Müdürü' },
  { id: 'klinik-hemsire', name: 'Klinik Araştırma Hemşiresi' },
  { id: 'proje-muduru', name: 'Proje Müdürü' },
  { id: 'saha-koordinatoru', name: 'Saha Koordinatörü' },
  { id: 'saha-koordinatoru-sorumlusu', name: 'Saha Koordinatörü Sorumlusu' },
  { id: 'smo-lead', name: 'SMO Lead' }
];

// Tarife gösterim formatını belirleyen yardımcı fonksiyon
const formatTariffDisplay = (tariff) => {
  if (!tariff) return '';

  const tariffType = tariff.type?.toUpperCase();

  if (tariffType === 'FTE') {
    return `${tariff.type} - ${tariff.value}`;
  } else {
    return `Saatlik - ${tariff.value}`;
  }
};

// Multi-select dropdown for roles
const MultiSelectRoleDropdown = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Seçiniz...",
  className = "",
  hasWarning = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
    };

    if (isOpen) {
      updatePosition();
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, selectedValues]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  const handleOptionClick = (optionName) => {
    const newSelectedValues = selectedValues.includes(optionName)
      ? selectedValues.filter(name => name !== optionName)
      : [...selectedValues, optionName];

    onChange(newSelectedValues);
  };

  const getSelectedText = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === 1) {
      return selectedValues[0];
    }
    return `${selectedValues.length} rol seçildi`;
  };

  const filteredOptions = options.filter(option => {
    const searchLower = searchTerm.toLocaleLowerCase('tr-TR');
    return option.name.toLocaleLowerCase('tr-TR').includes(searchLower);
  });

  return (
    <div className={`${styles.multiSelectContainer} ${className}`} ref={dropdownRef}>
      <div
        ref={triggerRef}
        className={`${styles.multiSelectTrigger} ${hasWarning ? styles.warningInput : ''}`}
        onClick={handleToggle}
      >
        <span className={styles.multiSelectText}>{getSelectedText()}</span>
        <FaChevronDown className={`${styles.multiSelectIcon} ${isOpen ? styles.rotated : ''}`} />
      </div>

      {isOpen && (
        <div 
          className={styles.multiSelectDropdown}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          <div className={styles.searchContainer}>
            <FaSearch className={styles.searchIcon} />
            <input
              ref={searchInputRef}
              type="text"
              className={styles.searchInput}
              placeholder="Rol ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                className={styles.clearSearchButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchTerm('');
                  searchInputRef.current?.focus();
                }}
              >
                <FaTimes />
              </button>
            )}
          </div>
          <div className={styles.optionsContainer}>
            {filteredOptions.length === 0 ? (
              <div className={styles.multiSelectOption}>
                {searchTerm ? 'Sonuç bulunamadı' : 'Seçenek bulunamadı'}
              </div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.id}
                  className={`${styles.multiSelectOption} ${selectedValues.includes(option.name) ? styles.selected : ''}`}
                  onClick={() => handleOptionClick(option.name)}
                >
                  <span className={styles.optionText}>
                    {option.name}
                  </span>
                  {selectedValues.includes(option.name) && (
                    <FaCheck className={styles.checkIcon} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Multi-select dropdown komponenti
const MultiSelectDropdown = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Seçiniz...",
  className = "",
  hasWarning = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update dropdown position whenever it's open or when selections change
  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
    };

    if (isOpen) {
      updatePosition();
      // Focus on search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);

      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, selectedValues]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  // Centers holds several documents for the same hospital, so the same name was
  // listed over and over. They are merged into one row per label; every id the
  // row stands for is kept so a worker saved against any copy still reads as
  // selected, and deselecting clears all of them.
  const dedupedOptions = useMemo(
    () => dedupeCenters(options, option => `${option.Adı || ''} - ${option.Bilgi || ''} - ${option.isDeleted ? 'deleted' : ''}`).options,
    [options]
  );

  const isOptionSelected = (option) => optionMatchesAnyCenterId(option, selectedValues);

  const handleOptionClick = (option) => {
    const groupIds = option.duplicateIds || [option.id];

    // Selecting stores the canonical id; deselecting drops every copy, so a
    // value saved earlier against a duplicate is not left behind.
    const newSelectedValues = isOptionSelected(option)
      ? selectedValues.filter(id => !groupIds.includes(id))
      : [...selectedValues, option.id];

    onChange(newSelectedValues);
  };

  const getSelectedText = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === 1) {
      const selectedOption = options.find(opt => opt.id === selectedValues[0]);
      return selectedOption ? selectedOption.Adı : placeholder;
    }
    return `${selectedValues.length} merkez seçildi`;
  };

  // Filter options based on search term (Turkish locale aware, case-insensitive)
  const filteredOptions = dedupedOptions.filter(option => {
    const searchLower = searchTerm.toLocaleLowerCase('tr-TR');
    const nameMatch = option.Adı?.toLocaleLowerCase('tr-TR').includes(searchLower);
    const infoMatch = option.Bilgi?.toLocaleLowerCase('tr-TR').includes(searchLower);
    return nameMatch || infoMatch;
  });

  return (
    <div className={`${styles.multiSelectContainer} ${className}`} ref={dropdownRef}>
      <div
        ref={triggerRef}
        className={`${styles.multiSelectTrigger} ${hasWarning ? styles.warningInput : ''}`}
        onClick={handleToggle}
      >
        <span className={styles.multiSelectText}>{getSelectedText()}</span>
        <FaChevronDown className={`${styles.multiSelectIcon} ${isOpen ? styles.rotated : ''}`} />
      </div>

      {isOpen && (
        <div 
          className={styles.multiSelectDropdown}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          <div className={styles.searchContainer}>
            <FaSearch className={styles.searchIcon} />
            <input
              ref={searchInputRef}
              type="text"
              className={styles.searchInput}
              placeholder="Merkez ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                className={styles.clearSearchButton}
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchTerm('');
                  searchInputRef.current?.focus();
                }}
              >
                <FaTimes />
              </button>
            )}
          </div>
          <div className={styles.optionsContainer}>
            {filteredOptions.length === 0 ? (
              <div className={styles.multiSelectOption}>
                {searchTerm ? 'Sonuç bulunamadı' : 'Seçenek bulunamadı'}
              </div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.id}
                  className={`${styles.multiSelectOption} ${isOptionSelected(option) ? styles.selected : ''} ${option.isDeleted ? styles.deletedCenter : ''}`}
                  onClick={() => handleOptionClick(option)}
                  >
                  <span className={styles.optionText}>
                    {option.Adı} - {option.Bilgi || ''}
                    {option.isDeleted && <span className={styles.deletedBadge}> (Silinmiş)</span>}
                  </span>
                  {isOptionSelected(option) && (
                    <FaCheck className={styles.checkIcon} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

    </div>
  );
};

const AddWorkerModal = ({
  onClose,
  selectedEmployee,
  setSelectedEmployee,
  selectedRate,
  setSelectedRate,
  selectedRole,
  setSelectedRole,
  selectedCenters,
  setSelectedCenters,
  centerTariffs,
  setCenterTariffs,
  selectedInfo,
  setSelectedInfo,
  selectedWorkerType,
  setSelectedWorkerType,
  selectedBlindStatus,
  setSelectedBlindStatus,
  handleAddWorker,
  loading,
  availableEmployees,
  tariffs,
  centers,
  hasManagerWarning,
  isOnlyHourly,
  isTestProject
}) => {
  const selectedCentersRef = useRef(null);
  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaUserPlus className={styles.modalIcon} />
            Projeye Çalışan Ekle
          </h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>
        <div className={styles.modalContent}>
          <div className={styles.formGroup}>
            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaIdCard style={{ marginRight: '8px' }} />
                Çalışan
              </label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className={styles.select}
              >
                <option value="">Lütfen Seçiniz</option>
                {availableEmployees
                  .filter(emp => emp.Active === 1 || emp.Active === undefined)
                  .sort((a, b) => `${a.eName} ${a.eSurname}`.localeCompare(`${b.eName} ${b.eSurname}`))
                  .map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.eName} {emp.eSurname}
                    </option>
                  ))}
              </select>
            </div>

            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaUserTag style={{ marginRight: '8px' }} />
                Roller
              </label>
              <MultiSelectRoleDropdown
                options={ROLE_OPTIONS}
                selectedValues={selectedRole}
                onChange={setSelectedRole}
                placeholder="Lütfen rol seçiniz"
                hasWarning={hasManagerWarning}
              />
              {hasManagerWarning && (
                <p className={styles.warningText}>Bu merkez ve rol için zaten bir çalışan atanmış!</p>
              )}
            </div>

            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaBuilding style={{ marginRight: '8px' }} />
                Merkezler
              </label>
              <MultiSelectDropdown
                options={[...centers].sort((a, b) => a.Adı.localeCompare(b.Adı, 'tr'))}
                selectedValues={selectedCenters}
                onChange={(newCenters) => {
                  setSelectedCenters(newCenters);
                  // Yeni merkez eklendiğinde scroll'u en alta kaydır
                  if (newCenters.length > selectedCenters.length && selectedCentersRef.current) {
                    setTimeout(() => {
                      selectedCentersRef.current.scrollTop = selectedCentersRef.current.scrollHeight;
                    }, 100);
                  }
                }}
                placeholder="Lütfen merkez seçiniz"
                hasWarning={hasManagerWarning}
              />
            </div>

            {selectedCenters.length > 0 && (
              <div className={styles.formControl}>
                <label className={styles.label}>
                  <FaCheck style={{ marginRight: '8px' }} />
                  Seçilen Merkezler
                </label>
                <div className={styles.selectedCentersListSimple}>
                  {selectedCenters.map(centerId => {
                    const center = centers.find(c => c.id === centerId);
                    return center ? (
                      <div key={centerId} className={styles.centerChipSimple}>
                        <FaBuilding className={styles.centerChipIcon} />
                        <span>{center.Adı}</span>
                        <button
                          type="button"
                          className={styles.removeCenterChip}
                          onClick={() => {
                            setSelectedCenters(selectedCenters.filter(id => id !== centerId));
                          }}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
                <div className={styles.fteInfoNote}>
                  <FaInfoCircle style={{ marginRight: '6px' }} />
                  FTE yönetimi için Çalışan Analizi sayfasını kullanın
                </div>
              </div>
            )}

            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaInfoCircle style={{ marginRight: '8px' }} />
                Bilgi
              </label>
              <textarea
                value={selectedInfo}
                onChange={(e) => setSelectedInfo(e.target.value)}
                className={styles.textarea}
                placeholder="Ek bilgi giriniz..."
              />
            </div>

            {/* Primer/Backup Seçimi */}
            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaUserTag style={{ marginRight: '8px' }} />
                Çalışan Tipi
              </label>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedWorkerType === 'primer'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWorkerType('primer');
                      } else {
                        setSelectedWorkerType('');
                      }
                    }}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Primer</span>
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedWorkerType === 'backup'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWorkerType('backup');
                      } else {
                        setSelectedWorkerType('');
                      }
                    }}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Backup</span>
                </label>
              </div>
            </div>

            {/* Blinded/Unblinded Seçimi */}
            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaUserTag style={{ marginRight: '8px' }} />
                Kör Çalışma Durumu
              </label>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedBlindStatus === 'blinded'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBlindStatus('blinded');
                      } else {
                        setSelectedBlindStatus('');
                      }
                    }}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Blinded</span>
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedBlindStatus === 'unblinded'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBlindStatus('unblinded');
                      } else {
                        setSelectedBlindStatus('');
                      }
                    }}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Unblinded</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>
            Kapat
          </button>
          <button
            className={styles.saveButton}
            onClick={handleAddWorker}
            disabled={loading || !selectedEmployee || selectedRole.length === 0 || selectedCenters.length === 0}
          >
            {loading ? (
              <>
                <div className={styles.loadingSpinner}></div>
                Ekleniyor...
              </>
            ) : 'Çalışan Ekle'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditWorkerModal = ({
  onClose,
  worker,
  selectedRate,
  setSelectedRate,
  selectedRole,
  setSelectedRole,
  selectedCenters,
  setSelectedCenters,
  centerTariffs,
  setCenterTariffs,
  selectedInfo,
  setSelectedInfo,
  selectedWorkerType,
  setSelectedWorkerType,
  selectedBlindStatus,
  setSelectedBlindStatus,
  selectedStatus,
  setSelectedStatus,
  handleEditWorker,
  loading,
  tariffs,
  centers,
  hasManagerWarning,
  isOnlyHourly,
  isTestProject
}) => {
  const selectedCentersRef = useRef(null);
  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaUserPlus className={styles.modalIcon} />
            Çalışan Bilgilerini Düzenle
          </h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>
        <div className={styles.modalContent}>
          <div className={styles.formGroup}>
            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaUserTag style={{ marginRight: '8px' }} />
                Roller
              </label>
              <MultiSelectRoleDropdown
                options={ROLE_OPTIONS}
                selectedValues={selectedRole}
                onChange={setSelectedRole}
                placeholder="Lütfen rol seçiniz"
                hasWarning={hasManagerWarning}
              />
              {hasManagerWarning && (
                <p className={styles.warningText}>Bu merkez ve rol için zaten bir çalışan atanmış!</p>
              )}
            </div>

            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaBuilding style={{ marginRight: '8px' }} />
                Merkezler
              </label>
              <MultiSelectDropdown
                options={[...centers].sort((a, b) => a.Adı.localeCompare(b.Adı, 'tr'))}
                selectedValues={selectedCenters}
                onChange={(newCenters) => {
                  setSelectedCenters(newCenters);
                  // Yeni merkez eklendiğinde scroll'u en alta kaydır
                  if (newCenters.length > selectedCenters.length && selectedCentersRef.current) {
                    setTimeout(() => {
                      selectedCentersRef.current.scrollTop = selectedCentersRef.current.scrollHeight;
                    }, 100);
                  }
                }}
                placeholder="Lütfen merkez seçiniz"
                hasWarning={hasManagerWarning}
              />
            </div>

            {selectedCenters.length > 0 && (
              <div className={styles.formControl}>
                <label className={styles.label}>
                  <FaCheck style={{ marginRight: '8px' }} />
                  Seçilen Merkezler
                </label>
                <div className={styles.selectedCentersListSimple}>
                  {selectedCenters.map(centerId => {
                    const center = centers.find(c => c.id === centerId);
                    return center ? (
                      <div key={centerId} className={styles.centerChipSimple}>
                        <FaBuilding className={styles.centerChipIcon} />
                        <span>{center.Adı}</span>
                        <button
                          type="button"
                          className={styles.removeCenterChip}
                          onClick={() => {
                            setSelectedCenters(selectedCenters.filter(id => id !== centerId));
                          }}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
                <div className={styles.fteInfoNote}>
                  <FaInfoCircle style={{ marginRight: '6px' }} />
                  FTE yönetimi için Çalışan Analizi sayfasını kullanın
                </div>
              </div>
            )}

            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaInfoCircle style={{ marginRight: '8px' }} />
                Bilgi
              </label>
              <textarea
                value={selectedInfo}
                onChange={(e) => setSelectedInfo(e.target.value)}
                className={styles.textarea}
                placeholder="Ek bilgi giriniz..."
              />
            </div>

            {/* Primer/Backup Seçimi */}
            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaUserTag style={{ marginRight: '8px' }} />
                Çalışan Tipi
              </label>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedWorkerType === 'primer'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWorkerType('primer');
                      } else {
                        setSelectedWorkerType('');
                      }
                    }}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Primer</span>
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedWorkerType === 'backup'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWorkerType('backup');
                      } else {
                        setSelectedWorkerType('');
                      }
                    }}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Backup</span>
                </label>
              </div>
            </div>

            {/* Blinded/Unblinded Seçimi */}
            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaUserTag style={{ marginRight: '8px' }} />
                Kör Çalışma Durumu
              </label>
              <div className={styles.checkboxGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedBlindStatus === 'blinded'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBlindStatus('blinded');
                      } else {
                        setSelectedBlindStatus('');
                      }
                    }}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Blinded</span>
                </label>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selectedBlindStatus === 'unblinded'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBlindStatus('unblinded');
                      } else {
                        setSelectedBlindStatus('');
                      }
                    }}
                    className={styles.checkbox}
                  />
                  <span className={styles.checkboxText}>Unblinded</span>
                </label>
              </div>
            </div>

            <div className={styles.formControl}>
              <label className={styles.label}>
                <FaUserTag style={{ marginRight: '8px' }} />
                Durum
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className={styles.select}
              >
                <option value="active">Aktif</option>
                <option value="passive">Pasif</option>
              </select>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>
            Kapat
          </button>
          <button
            className={styles.saveButton}
            onClick={handleEditWorker}
            disabled={loading || selectedRole.length === 0 || selectedCenters.length === 0}
          >
            {loading ? (
              <>
                <div className={styles.loadingSpinner}></div>
                Güncelleniyor...
              </>
            ) : 'Güncelle'}
          </button>
        </div>
      </div>
    </div>
  );
};

const WorkersModal = ({ project, onClose }) => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [showAddWorkerModal, setShowAddWorkerModal] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedRate, setSelectedRate] = useState('');
  const [selectedRole, setSelectedRole] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [centers, setCenters] = useState([]);
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [centerTariffs, setCenterTariffs] = useState({});
  const [selectedInfo, setSelectedInfo] = useState('');
  const [selectedWorkerType, setSelectedWorkerType] = useState('');
  const [selectedBlindStatus, setSelectedBlindStatus] = useState('');
  const [hasManagerWarning, setHasManagerWarning] = useState(false);
  const [isOnlyHourly, setIsOnlyHourly] = useState(false);

  // Maps every duplicate Centers document to the one option shown for it, so
  // checks across centers treat the copies as a single center.
  const canonicalCenterIdById = useMemo(
    () => dedupeCenters(
      centers,
      center => `${center.Adı || ''} - ${center.Bilgi || ''} - ${center.isDeleted ? 'deleted' : ''}`
    ).canonicalIdById,
    [centers]
  );
  
  // Check if this is a test project
  const isTestProject = project?.isTestProject === true || project?.isTestProject === 'true';
  const [filters, setFilters] = useState({
    name: '',
    surname: '',
    role: '',
    rate: '',
    center: '',
    info: '',
    assignedAt: '',
    status: ''
  });
  const [activeFilter, setActiveFilter] = useState(null);
  const filterInputRefs = useRef({});
  const [showEditWorkerModal, setShowEditWorkerModal] = useState(false);
  const [workerToEdit, setWorkerToEdit] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [showEmployeeFileUploadModal, setShowEmployeeFileUploadModal] = useState(false);
  const [selectedEmployeeForUpload, setSelectedEmployeeForUpload] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    if (timestamp.seconds) {
      const date = new Date(timestamp.seconds * 1000);
      return date.toLocaleDateString('tr-TR');
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('tr-TR');
    }
    return timestamp;
  };

  const fetchProjectWorkers = async () => {
    try {
      if (!project || !project.id) {
        setLoading(false);
        return;
      }

      const projectDoc = await getDoc(doc(db, 'Projects', project.id));
      const projectData = projectDoc.data();

      if (!projectData || !projectData.employees) {
        setWorkers([]);
        setLoading(false);
        return;
      }

      const workerDetailsPromises = projectData.employees.map(async (employee) => {
        if (typeof employee === 'string') {
          const employeeDoc = await getDoc(doc(db, 'Employees', employee));
          if (employeeDoc.exists()) {
            return {
              id: employeeDoc.id,
              ...employeeDoc.data(),
              assignedAt: projectData.employees.find(e => e.id === employee)?.assignedAt
            };
          }
        } else {
          return employee;
        }
      });

      const workerDetails = await Promise.all(workerDetailsPromises);
      setWorkers(workerDetails.filter(Boolean));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching project workers:', error);
      toast.error('Çalışan bilgileri yüklenirken hata oluştu!');
      setLoading(false);
    }
  };

  const fetchTariffs = async () => {
    try {
      if (!project || !project.id) {
        setTariffs([]);
        setIsOnlyHourly(false);
        return;
      }

      const tariffsRef = collection(db, 'Tariffs');
      const querySnapshot = await getDocs(query(tariffsRef, where('project', '==', project.id)));
      const tariffsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Hem FTE hem saat var mı kontrol et
      const hasFTE = tariffsData.some(t => t.type?.toUpperCase() === 'FTE');
      const hasHourly = tariffsData.some(t => t.type?.toUpperCase() !== 'FTE');

      // Tüm tarifeleri sakla - filtrelemeyi dropdown seviyesinde yapacağız
      setTariffs(tariffsData);

      // Sadece saat varsa flag'i set et (tüm merkezler için otomatik seçim yapılacak)
      setIsOnlyHourly(!hasFTE && hasHourly);
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error('Tarife listesi yüklenirken hata oluştu!');
      setTariffs([]);
      setIsOnlyHourly(false);
    }
  };

  const fetchCenters = async () => {
    try {
      // Fetch all centers from Centers collection
      const centersRef = collection(db, 'Centers');
      const querySnapshot = await getDocs(centersRef);
      const centersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Also include centers that are used in the current project but might be deleted from Centers collection
      const centerMap = new Map();
      
      // Add all centers from Centers collection
      centersData.forEach(center => {
        centerMap.set(center.id, center);
      });
      
      // Add centers from current project's employees (if they don't exist in Centers collection)
      if (project && project.id) {
        try {
          const projectDoc = await getDoc(doc(db, 'Projects', project.id));
          const projectData = projectDoc.data();
          
          if (projectData && projectData.employees) {
            projectData.employees.forEach(emp => {
              if (emp.centerId && !centerMap.has(emp.centerId)) {
                // This center is deleted from Centers collection but still exists in project
                centerMap.set(emp.centerId, {
                  id: emp.centerId,
                  Adı: emp.centerName || 'Silinmiş Merkez',
                  Bilgi: '(Merkez silinmiş - sadece görüntüleme)',
                  Durum: 'İnaktif',
                  İl: '',
                  İlçe: '',
                  isDeleted: true // Flag to indicate this center is deleted
                });
              }
            });
          }
        } catch (error) {
          console.error('Error fetching project data for centers:', error);
        }
      }
      
      // Convert map to array and sort
      const allCenters = Array.from(centerMap.values()).sort((a, b) => {
        // Put deleted centers at the end
        if (a.isDeleted && !b.isDeleted) return 1;
        if (!a.isDeleted && b.isDeleted) return -1;
        return (a.Adı || '').localeCompare(b.Adı || '', 'tr');
      });
      
      setCenters(allCenters);
      
      console.log('📍 Fetched centers:', allCenters.length, 'total');
      console.log('📍 Deleted centers included:', allCenters.filter(c => c.isDeleted).length);
    } catch (error) {
      console.error('Error fetching centers:', error);
      toast.error('Merkez listesi yüklenirken hata oluştu!');
    }
  };

  useEffect(() => {
    fetchProjectWorkers();
  }, [project]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const employeesRef = collection(db, 'Employees');
        const querySnapshot = await getDocs(employeesRef);
        const employees = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAvailableEmployees(employees);

        await fetchTariffs();
        await fetchCenters();
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Veri yüklenirken hata oluştu!');
      }
    };

    fetchData();
  }, []);

  // Check for existing manager whenever role or centers change
  useEffect(() => {
    // roles artık array, herhangi biri manager rolü mü kontrol et
    const hasManagerRole = Array.isArray(selectedRole) && selectedRole.some(role =>
      role === 'Saha Koordinatörü Sorumlusu' || role === 'Proje Müdürü'
    );

    if (!hasManagerRole || selectedCenters.length === 0) {
      setHasManagerWarning(false);
      return;
    }

    // Duplicate Centers documents are one center as far as the user is
    // concerned, so an existing manager saved against another copy of the same
    // center still counts as a clash.
    const selectedCanonicalIds = new Set(
      selectedCenters.map(id => resolveCanonicalCenterId(canonicalCenterIdById, id))
    );

    const existingManager = workers.find(worker =>
      (worker.role === 'Saha Koordinatörü Sorumlusu' || worker.role === 'Proje Müdürü') &&
      selectedCanonicalIds.has(resolveCanonicalCenterId(canonicalCenterIdById, worker.centerId))
    );

    if (existingManager) {
      setHasManagerWarning(true);
      toast.error('Bu merkez için daha önceden bir sorumlu atanmış!');
    } else {
      setHasManagerWarning(false);
    }
  }, [selectedRole, selectedCenters, workers, canonicalCenterIdById]);

  const handleAddWorker = async () => {
    if (!selectedEmployee || selectedRole.length === 0 || selectedCenters.length === 0) {
      toast.error('Lütfen gerekli alanları doldurunuz!');
      return;
    }

    // Default test tariff for test projects
    const defaultTestTariff = {
      id: 'test-tariff-default',
      type: 'FTE',
      value: '0.7',
      price: 700,
      currency: 'TRY'
    };

    // Eğer sadece saatlik tarife varsa, otomatik olarak ilk tarifeyi kullan
    let effectiveCenterTariffs = centerTariffs;
    let useDefaultTestTariff = false;
    
    if (isTestProject) {
      // For test projects, use default tariff if none selected
      useDefaultTestTariff = true;
      effectiveCenterTariffs = {};
      selectedCenters.forEach(centerId => {
        effectiveCenterTariffs[centerId] = centerTariffs[centerId] || 'test-tariff-default';
      });
    } else if (isOnlyHourly && tariffs.length > 0) {
      const hourlyTariff = tariffs[0];
      effectiveCenterTariffs = {};
      selectedCenters.forEach(centerId => {
        effectiveCenterTariffs[centerId] = hourlyTariff.id;
      });
    } else {
      // No tariff required — rate managed on a separate page
      effectiveCenterTariffs = {};
    }

    if (hasManagerWarning) {
      toast.error('Bu merkez için daha önceden bir sorumlu atanmış!');
      return;
    }

    setLoading(true);
    try {
      const projectRef = doc(db, 'Projects', project.id);
      const employeeRef = doc(db, 'Employees', selectedEmployee);

      // Get current data for both documents
      const projectDoc = await getDoc(projectRef);
      const employeeDoc = await getDoc(employeeRef);
      const currentProjectData = projectDoc.data();
      const currentEmployeeData = employeeDoc.data();

      // Check if employee is already assigned to project
      const currentEmployees = currentProjectData.employees || [];
      const isAlreadyAssigned = currentEmployees.some(e => e.id === selectedEmployee);

      if (isAlreadyAssigned) {
        toast.error('Bu çalışan zaten bu projede çalışıyor!');
        setLoading(false);
        return;
      }

      // Prepare new employee data for project - create separate entries for each center AND each role
      const newEmployeeEntries = [];
      for (const role of selectedRole) {
        for (const centerId of selectedCenters) {
          let selectedTariff;
          if (useDefaultTestTariff && effectiveCenterTariffs[centerId] === 'test-tariff-default') {
            selectedTariff = defaultTestTariff;
          } else {
            selectedTariff = tariffs.find(t => t.id === effectiveCenterTariffs[centerId]);
          }
          newEmployeeEntries.push({
            id: selectedEmployee,
            name: currentEmployeeData.eName,
            surname: currentEmployeeData.eSurname,
            rate: selectedTariff ? {
              id: selectedTariff.id,
              type: selectedTariff.type,
              value: selectedTariff.value,
              price: selectedTariff.price,
              currency: selectedTariff.currency
            } : null,
            role: role,
            centerId: centerId,
            centerName: centers.find(c => c.id === centerId)?.Adı,
            assignedAt: new Date(),
            info: selectedInfo,
            workerType: selectedWorkerType,
            blindStatus: selectedBlindStatus,
            status: 'active'
          });
        }
      }

      const updatedEmployees = [...currentEmployees, ...newEmployeeEntries];

      // Prepare new project data for employee - create separate entries for each center AND each role
      const employeeProjects = currentEmployeeData.projects || [];
      const newProjectEntries = [];
      for (const role of selectedRole) {
        for (const centerId of selectedCenters) {
          let selectedTariff;
          if (useDefaultTestTariff && effectiveCenterTariffs[centerId] === 'test-tariff-default') {
            selectedTariff = defaultTestTariff;
          } else {
            selectedTariff = tariffs.find(t => t.id === effectiveCenterTariffs[centerId]);
          }
          newProjectEntries.push({
            id: project.id,
            titleShort: project.titleShort,
            titleLong: project.titleLong,
            projectDate: project.projectDate,
            rate: selectedTariff ? {
              id: selectedTariff.id,
              type: selectedTariff.type,
              value: selectedTariff.value,
              price: selectedTariff.price,
              currency: selectedTariff.currency
            } : null,
            role: role,
            centerId: centerId,
            centerName: centers.find(c => c.id === centerId)?.Adı,
            assignedAt: new Date(),
            workerType: selectedWorkerType,
            blindStatus: selectedBlindStatus,
            status: 'active'
          });
        }
      }

      const updatedProjects = [...employeeProjects, ...newProjectEntries];

      // Update both documents
      await updateDoc(projectRef, {
        employees: updatedEmployees
      });

      await updateDoc(employeeRef, {
        projects: updatedProjects
      });

      toast.success('Çalışan başarıyla projeye eklendi!');
      setShowAddWorkerModal(false);
      setSelectedRow(null); // Clear selected row
      await fetchProjectWorkers(); // Refresh the workers list

      // Reset form
      setSelectedEmployee('');
      setSelectedRate('');
      setSelectedRole([]);
      setSelectedCenters([]);
      setCenterTariffs({});
      setSelectedInfo('');
      setSelectedWorkerType('');
      setSelectedBlindStatus('');

    } catch (error) {
      console.error('Error adding worker:', error);
      toast.error('Çalışan eklenirken bir hata oluştu!');
    }
    setLoading(false);
  };

  const handleEditWorker = async () => {
    if (selectedRole.length === 0 || selectedCenters.length === 0) {
      toast.error('Lütfen gerekli alanları doldurunuz!');
      return;
    }

    // Default test tariff for test projects
    const defaultTestTariff = {
      id: 'test-tariff-default',
      type: 'FTE',
      value: '0.7',
      price: 700,
      currency: 'TRY'
    };

    // Eğer sadece saatlik tarife varsa, otomatik olarak ilk tarifeyi kullan
    let effectiveCenterTariffs = centerTariffs;
    let useDefaultTestTariff = false;
    
    if (isTestProject) {
      // For test projects, use default tariff if none selected
      useDefaultTestTariff = true;
      effectiveCenterTariffs = {};
      selectedCenters.forEach(centerId => {
        effectiveCenterTariffs[centerId] = centerTariffs[centerId] || 'test-tariff-default';
      });
    } else if (isOnlyHourly && tariffs.length > 0) {
      const hourlyTariff = tariffs[0];
      effectiveCenterTariffs = {};
      selectedCenters.forEach(centerId => {
        effectiveCenterTariffs[centerId] = hourlyTariff.id;
      });
    } else {
      // No tariff required — rate managed on a separate page
      effectiveCenterTariffs = {};
    }

    if (hasManagerWarning) {
      toast.error('Bu merkez için daha önceden bir sorumlu atanmış!');
      return;
    }

    setLoading(true);
    try {
      const projectRef = doc(db, 'Projects', project.id);
      const employeeRef = doc(db, 'Employees', workerToEdit.id);

      // Get current data for both documents
      const projectDoc = await getDoc(projectRef);
      const employeeDoc = await getDoc(employeeRef);
      const currentProjectData = projectDoc.data();
      const currentEmployeeData = employeeDoc.data();

      // Remove only the entries for this worker in the centers that were originally loaded in the modal
      // workerToEdit.centers contains the original centers that were shown when modal opened
      const originalCenterIds = workerToEdit.centers.map(c => c.centerId);
      const updatedEmployees = currentProjectData.employees.filter(emp =>
        !(emp.id === workerToEdit.id && originalCenterIds.includes(emp.centerId))
      );

      // Preserve existing per-center rates so edits don't wipe rates managed on the FTE Yönetimi page
      const existingRateByCenter = {};
      (workerToEdit.centers || []).forEach(c => {
        if (c.centerId && c.rate) existingRateByCenter[c.centerId] = c.rate;
      });

      // Add new entries for each selected center AND each role
      const newEmployeeEntries = [];
      for (const role of selectedRole) {
        for (const centerId of selectedCenters) {
          let selectedTariff;
          if (useDefaultTestTariff && effectiveCenterTariffs[centerId] === 'test-tariff-default') {
            selectedTariff = defaultTestTariff;
          } else {
            selectedTariff = tariffs.find(t => t.id === effectiveCenterTariffs[centerId]);
          }
          const rate = selectedTariff ? {
            id: selectedTariff.id,
            type: selectedTariff.type,
            value: selectedTariff.value,
            price: selectedTariff.price,
            currency: selectedTariff.currency
          } : (existingRateByCenter[centerId] || null);
          newEmployeeEntries.push({
            id: workerToEdit.id,
            name: workerToEdit.name || workerToEdit.eName,
            surname: workerToEdit.surname || workerToEdit.eSurname,
            rate,
            role: role,
            centerId: centerId,
            centerName: centers.find(c => c.id === centerId)?.Adı,
            assignedAt: workerToEdit.assignedAt || new Date(),
            info: selectedInfo,
            workerType: selectedWorkerType,
            blindStatus: selectedBlindStatus,
            status: selectedStatus
          });
        }
      }

      const finalEmployees = [...updatedEmployees, ...newEmployeeEntries];

      console.log('🔄 Updating worker in project:', {
        projectId: project.id,
        projectTitle: project.titleShort || project.titleLong,
        workerId: workerToEdit.id,
        workerName: `${workerToEdit.name || workerToEdit.eName} ${workerToEdit.surname || workerToEdit.eSurname}`,
        selectedWorkerType,
        selectedBlindStatus,
        newEmployeeEntries: newEmployeeEntries.map(e => ({
          centerId: e.centerId,
          centerName: e.centerName,
          role: e.role,
          workerType: e.workerType,
          blindStatus: e.blindStatus
        }))
      });

      // Update project data in employee - remove only entries for this project in the original centers
      // originalCenterIds already defined above, reuse it
      const updatedProjects = currentEmployeeData.projects.filter(proj => 
        !(proj.id === project.id && originalCenterIds.includes(proj.centerId))
      );

      const newProjectEntries = [];
      for (const role of selectedRole) {
        for (const centerId of selectedCenters) {
          let selectedTariff;
          if (useDefaultTestTariff && effectiveCenterTariffs[centerId] === 'test-tariff-default') {
            selectedTariff = defaultTestTariff;
          } else {
            selectedTariff = tariffs.find(t => t.id === effectiveCenterTariffs[centerId]);
          }
          const rate = selectedTariff ? {
            id: selectedTariff.id,
            type: selectedTariff.type,
            value: selectedTariff.value,
            price: selectedTariff.price,
            currency: selectedTariff.currency
          } : (existingRateByCenter[centerId] || null);
          newProjectEntries.push({
            id: project.id,
            titleShort: project.titleShort,
            titleLong: project.titleLong,
            projectDate: project.projectDate,
            rate,
            role: role,
            centerId: centerId,
            centerName: centers.find(c => c.id === centerId)?.Adı,
            assignedAt: workerToEdit.assignedAt || new Date(),
            workerType: selectedWorkerType,
            blindStatus: selectedBlindStatus,
            status: selectedStatus
          });
        }
      }

      const finalProjects = [...updatedProjects, ...newProjectEntries];

      console.log('🔄 Updating project in employee:', {
        employeeId: workerToEdit.id,
        employeeName: `${workerToEdit.name || workerToEdit.eName} ${workerToEdit.surname || workerToEdit.eSurname}`,
        projectId: project.id,
        projectTitle: project.titleShort || project.titleLong,
        selectedWorkerType,
        selectedBlindStatus,
        newProjectEntries: newProjectEntries.map(p => ({
          centerId: p.centerId,
          centerName: p.centerName,
          role: p.role,
          workerType: p.workerType,
          blindStatus: p.blindStatus
        }))
      });

      // Update both documents
      await updateDoc(projectRef, {
        employees: finalEmployees
      });

      await updateDoc(employeeRef, {
        projects: finalProjects
      });

      toast.success('Çalışan bilgileri başarıyla güncellendi!');
      setShowEditWorkerModal(false);
      setSelectedRow(null); // Clear selected row to force refresh
      await fetchProjectWorkers(); // Refresh the workers list

      // Reset form
      setSelectedRate('');
      setSelectedRole([]);
      setSelectedCenters([]);
      setCenterTariffs({});
      setSelectedInfo('');
      setSelectedWorkerType('');
      setSelectedBlindStatus('');
      setSelectedStatus('active');
      setWorkerToEdit(null);

    } catch (error) {
      console.error('Error updating worker:', error);
      toast.error('Çalışan güncellenirken bir hata oluştu!');
    }
    setLoading(false);
  };

  const handleChangeWorkerStatus = async (worker) => {
    if (changing) return;

    const newStatus = worker.status === 'active' ? 'passive' : 'active';
    const statusAction = newStatus === 'active' ? 'aktif' : 'pasif';

    if (window.confirm(`${worker.name || worker.eName} ${worker.surname || worker.eSurname} çalışanını ${statusAction} durumuna geçirmek istediğinize emin misiniz?`)) {
      setChanging(true);
      try {
        const projectRef = doc(db, 'Projects', project.id);
        const employeeRef = doc(db, 'Employees', worker.id);

        const projectDoc = await getDoc(projectRef);
        const employeeDoc = await getDoc(employeeRef);

        if (!projectDoc.exists() || !employeeDoc.exists()) {
          toast.error('Belge bulunamad!');
          setChanging(false);
          return;
        }

        const projectData = projectDoc.data();
        const employeeData = employeeDoc.data();

        // Update worker status in project
        const updatedProjectEmployees = projectData.employees.map(emp => {
          if (emp.id === worker.id && emp.centerId === worker.centerId) {
            return {
              ...emp,
              status: newStatus
            };
          }
          return emp;
        });

        // Update project status in employee
        const updatedEmployeeProjects = (employeeData.projects || []).map(proj => {
          if (proj.id === project.id && proj.centerId === worker.centerId) {
            return {
              ...proj,
              status: newStatus
            };
          }
          return proj;
        });

        await Promise.all([
          updateDoc(projectRef, { employees: updatedProjectEmployees }),
          updateDoc(employeeRef, { projects: updatedEmployeeProjects })
        ]);

        toast.success(`Çalışan durumu başarıyla ${statusAction} olarak güncellendi!`);
        await fetchProjectWorkers();
      } catch (error) {
        console.error('Error changing worker status:', error);
        toast.error('Çalışan durumu değiştirilirken bir hata oluştu!');
      }
      setChanging(false);
    }
  };

  const filterWorkers = (workers) => {
    return workers.filter(worker => {
      return (
        (!filters.name || (worker.name || worker.eName || '').toLowerCase().includes(filters.name.toLowerCase())) &&
        (!filters.surname || (worker.surname || worker.eSurname || '').toLowerCase().includes(filters.surname.toLowerCase())) &&
        (!filters.role || (worker.role || '').toLowerCase().includes(filters.role.toLowerCase())) &&
        (!filters.rate || (worker.rate?.type || '').toLowerCase().includes(filters.rate.toLowerCase())) &&
        (!filters.center || (worker.centerName || '').toLowerCase().includes(filters.center.toLowerCase())) &&
        (!filters.info || (worker.info || '').toLowerCase().includes(filters.info.toLowerCase())) &&
        (!filters.assignedAt || formatDate(worker.assignedAt).includes(filters.assignedAt)) &&
        (!filters.status || (worker.status || 'active').toLowerCase().includes(filters.status.toLowerCase()))
      );
    });
  };

  const ColumnFilter = ({ column }) => {
    const isActive = activeFilter === column;
    const inputRef = useRef(null);

    useEffect(() => {
      if (isActive && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isActive]);

    return (
      <div className={styles.columnFilterContainer}>
        <span>{column}</span>
        <button
          className={styles.filterButton}
          onClick={(e) => {
            e.stopPropagation();
            setActiveFilter(isActive ? null : column);
          }}
        >
          <FaSearch className={`${styles.filterIcon} ${isActive ? styles.activeFilter : ''}`} />
        </button>
        {isActive && (
          <div className={styles.filterInputContainer}>
            <input
              ref={inputRef}
              type="text"
              value={filters[column.toLowerCase()]}
              onChange={(e) => {
                e.stopPropagation();
                setFilters(prev => ({
                  ...prev,
                  [column.toLowerCase()]: e.target.value
                }));
              }}
              onClick={(e) => e.stopPropagation()}
              className={styles.filterInput}
              placeholder={`${column} ile filtrele...`}
            />
          </div>
        )}
      </div>
    );
  };

  const downloadExcel = () => {
    const dataToExport = filterWorkers(workers).map(worker => ({
      'Çalışan Adı': worker.name || worker.eName || '',
      'Çalışan Soyadı': worker.surname || worker.eSurname || '',
      'Rol': worker.role || '',
      'Tarife': worker.rate ? `${worker.rate.type} - ${worker.rate.value}` : '',
      'Merkez': worker.centerName || '',
      'Bilgi': worker.info || '',
      'Atanma Tarihi': formatDate(worker.assignedAt)
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Çalışanlar');
    XLSX.writeFile(workbook, `${project.titleShort}_Çalışan_Listesi.xlsx`);
  };

  const groupWorkersByEmployee = (workers) => {
    const grouped = {};
    workers.forEach(worker => {
      const key = worker.id;
      if (!grouped[key]) {
        grouped[key] = {
          id: worker.id,
          name: worker.name || worker.eName,
          surname: worker.surname || worker.eSurname,
          assignedAt: worker.assignedAt,
          centers: []
        };
      }
      grouped[key].centers.push({
        centerId: worker.centerId,
        centerName: worker.centerName,
        role: worker.role,
        rate: worker.rate,
        info: worker.info,
        status: worker.status || 'active',
        workerType: worker.workerType, // Primer/Backup bilgisi
        blindStatus: worker.blindStatus, // Blinded/Unblinded bilgisi
        assignedAt: worker.assignedAt // Tarih bilgisi (sıralama için)
      });
    });
    return Object.values(grouped);
  };


  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modalLarge}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaUsers className={styles.modalIcon} />
            Proje Çalışanları - {project.titleShort}
          </h2>
          <div className={styles.headerButtons}>
            <button
              className={styles.downloadButton}
              onClick={downloadExcel}
            >
              <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
            </button>
            <button
              className={styles.addButton}
              onClick={() => setShowAddWorkerModal(true)}
            >
              <FaUserPlus className={styles.buttonIcon} /> Projeye Çalışan Ekle
            </button>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Kapat"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {!loading && workers.length > 0 && selectedRow && (
          <div className={styles.selectedRowPanel}>
            <div className={styles.selectedRowInfo}>
              <h3>Seçili Çalışan: {selectedRow.name} {selectedRow.surname}</h3>
              <div className={styles.selectedRowDetails}>
                <span><strong>Rol:</strong> {(() => {
                  const uniqueRoles = [...new Set(selectedRow.centers.map(c => c.role).filter(Boolean))];
                  return uniqueRoles.length > 0 ? uniqueRoles.join(', ') : '-';
                })()}</span>
                <span><strong>Merkez Sayısı:</strong> {(() => {
                  const uniqueCenters = [...new Set(selectedRow.centers.map(c => c.centerId))];
                  return uniqueCenters.length;
                })()}</span>
                <span><strong>Toplam Atama:</strong> {selectedRow.centers.length}</span>
                <span><strong>Durum:</strong> {selectedRow.centers.every(c => c.status === 'passive') ? 'Pasif' : 'Aktif'}</span>
              </div>
            </div>
            <div className={styles.selectedRowActions}>
              <button
                className={styles.editButton}
                onClick={() => {
                  setWorkerToEdit(selectedRow);
                  const tariffsMap = {};
                  selectedRow.centers.forEach(center => {
                    tariffsMap[center.centerId] = center.rate?.id || '';
                  });
                  // Collect all unique roles from all centers
                  const uniqueRoles = [...new Set(selectedRow.centers.map(c => c.role).filter(Boolean))];
                  setSelectedRole(uniqueRoles);
                  // Collect unique center IDs (remove duplicates)
                  const uniqueCenterIds = [...new Set(selectedRow.centers.map(c => c.centerId))];
                  setSelectedCenters(uniqueCenterIds);
                  setCenterTariffs(tariffsMap);
                  setSelectedInfo(selectedRow.centers[0]?.info || '');
                  
                  // workerType ve blindStatus için tüm merkezlerdeki değerleri kontrol et
                  // Eğer hepsi aynıysa o değeri kullan, farklıysa en son güncellenen değeri kullan
                  const allWorkerTypes = selectedRow.centers.map(c => c.workerType).filter(Boolean);
                  const allBlindStatuses = selectedRow.centers.map(c => c.blindStatus).filter(Boolean);
                  
                  console.log('📝 Opening edit modal for worker:', {
                    workerName: `${selectedRow.name || selectedRow.eName} ${selectedRow.surname || selectedRow.eSurname}`,
                    allCenters: selectedRow.centers.map(c => ({
                      centerId: c.centerId,
                      centerName: c.centerName,
                      role: c.role,
                      workerType: c.workerType,
                      blindStatus: c.blindStatus,
                      assignedAt: c.assignedAt
                    }))
                  });
                  
                  // En son güncellenen merkezi bul (assignedAt'e göre)
                  const sortedCenters = [...selectedRow.centers].sort((a, b) => {
                    const dateA = a.assignedAt?.toDate?.() || a.assignedAt || new Date(0);
                    const dateB = b.assignedAt?.toDate?.() || b.assignedAt || new Date(0);
                    return dateB - dateA; // En yeni önce
                  });
                  
                  const selectedWT = sortedCenters[0]?.workerType || '';
                  const selectedBS = sortedCenters[0]?.blindStatus || '';
                  
                  console.log('📝 Setting modal values:', {
                    selectedWorkerType: selectedWT,
                    selectedBlindStatus: selectedBS,
                    fromCenter: sortedCenters[0]?.centerName
                  });
                  
                  setSelectedWorkerType(selectedWT);
                  setSelectedBlindStatus(selectedBS);
                  setSelectedStatus(selectedRow.centers[0]?.status || 'active');
                  setShowEditWorkerModal(true);
                }}
              >
                <FaEdit className={styles.buttonIcon} />
                Düzenle
              </button>
              <button
                className={styles.uploadButton}
                onClick={() => {
                  setSelectedEmployeeForUpload(selectedRow);
                  setShowEmployeeFileUploadModal(true);
                }}
              >
                <FaUpload className={styles.buttonIcon} />
                Dosya Yükle
              </button>
            </div>
          </div>
        )}

        <div className={styles.modalContentLarge}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p>Yükleniyor...</p>
            </div>
          ) : (
            <>
              {workers.length > 0 ? (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.th}>
                          <ColumnFilter column="Name" />
                        </th>
                        <th className={styles.th}>
                          <ColumnFilter column="Surname" />
                        </th>
                        <th className={styles.th}>
                          <ColumnFilter column="Role" />
                        </th>
                        <th className={styles.th}>
                          <ColumnFilter column="AssignedAt" />
                        </th>
                        <th className={styles.th}>
                          <ColumnFilter column="Status" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const groupedWorkers = groupWorkersByEmployee(filterWorkers(workers));
                        return groupedWorkers.length > 0 ? (
                          groupedWorkers.map((groupedWorker) => {
                            const allCentersPassive = groupedWorker.centers.every(center => center.status === 'passive');

                            return (
                              <React.Fragment key={groupedWorker.id}>
                                {/* Ana çalışan satırı */}
                                <tr
                                  className={`${styles.tr} ${allCentersPassive ? styles.passiveWorker : ''} ${selectedRow?.id === groupedWorker.id ? styles.selectedRow : ''}`}
                                  onClick={() => setSelectedRow(groupedWorker)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td className={styles.td} title={groupedWorker.name || ''}>
                                    {groupedWorker.name || ''}
                                  </td>
                                  <td className={styles.td} title={groupedWorker.surname || ''}>{groupedWorker.surname || ''}</td>
                                  <td className={styles.td}>
                                    <span className={styles.centerCount}>
                                      {(() => {
                                        const uniqueCenters = [...new Set(groupedWorker.centers.map(c => c.centerId))];
                                        const uniqueRoles = [...new Set(groupedWorker.centers.map(c => c.role))];
                                        return `${uniqueCenters.length} merkez, ${uniqueRoles.length} rol`;
                                      })()}
                                    </span>
                                  </td>
                                  <td className={styles.td} title={formatDate(groupedWorker.assignedAt)}>{formatDate(groupedWorker.assignedAt)}</td>
                                  <td className={styles.td}>
                                    <span className={`${styles.statusBadge} ${allCentersPassive ? styles.passiveStatus : styles.activeStatus}`}>
                                      {allCentersPassive ? 'Pasif' : 'Aktif'}
                                    </span>
                                  </td>
                                </tr>
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan="5"
                              className={styles.noDataCell}
                            >
                              <div className={styles.noDataContent}>
                                <FaUserTimes className={styles.noDataIcon} />
                                <p>Aramanızla eşleşen hiç çalışan yok</p>
                              </div>
                            </td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.noDataContainer}>
                  <FaUserTimes className={styles.noDataIcon} />
                  <p>Bu projede henüz çalışan bulunmamaktadır.</p>
                </div>
              )}
            </>
          )}
        </div>
        {showAddWorkerModal && (
          <AddWorkerModal
            onClose={() => {
              setShowAddWorkerModal(false);
              setSelectedEmployee('');
              setSelectedRole([]);
              setSelectedCenters([]);
              setCenterTariffs({});
              setSelectedInfo('');
              setSelectedWorkerType('');
              setSelectedBlindStatus('');
            }}
            selectedEmployee={selectedEmployee}
            setSelectedEmployee={setSelectedEmployee}
            selectedRate={selectedRate}
            setSelectedRate={setSelectedRate}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            selectedCenters={selectedCenters}
            setSelectedCenters={setSelectedCenters}
            centerTariffs={centerTariffs}
            setCenterTariffs={setCenterTariffs}
            selectedInfo={selectedInfo}
            setSelectedInfo={setSelectedInfo}
            selectedWorkerType={selectedWorkerType}
            setSelectedWorkerType={setSelectedWorkerType}
            selectedBlindStatus={selectedBlindStatus}
            setSelectedBlindStatus={setSelectedBlindStatus}
            handleAddWorker={handleAddWorker}
            loading={loading}
            availableEmployees={availableEmployees}
            tariffs={tariffs}
            centers={centers}
            hasManagerWarning={hasManagerWarning}
            isOnlyHourly={isOnlyHourly}
            isTestProject={isTestProject}
          />
        )}
        {showEditWorkerModal && workerToEdit && (
          <EditWorkerModal
            onClose={() => {
              setShowEditWorkerModal(false);
              setWorkerToEdit(null);
              setSelectedRate('');
              setSelectedRole([]);
              setSelectedCenters([]);
              setCenterTariffs({});
              setSelectedInfo('');
              setSelectedWorkerType('');
              setSelectedBlindStatus('');
              setSelectedStatus('active');
            }}
            worker={workerToEdit}
            selectedRate={selectedRate}
            setSelectedRate={setSelectedRate}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            selectedCenters={selectedCenters}
            setSelectedCenters={setSelectedCenters}
            centerTariffs={centerTariffs}
            setCenterTariffs={setCenterTariffs}
            selectedInfo={selectedInfo}
            setSelectedInfo={setSelectedInfo}
            selectedWorkerType={selectedWorkerType}
            setSelectedWorkerType={setSelectedWorkerType}
            selectedBlindStatus={selectedBlindStatus}
            setSelectedBlindStatus={setSelectedBlindStatus}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            handleEditWorker={handleEditWorker}
            loading={loading}
            tariffs={tariffs}
            centers={centers}
            hasManagerWarning={hasManagerWarning}
            isOnlyHourly={isOnlyHourly}
            isTestProject={isTestProject}
          />
        )}
        {showEmployeeFileUploadModal && selectedEmployeeForUpload && (
          <EmployeeFileUploadModal
            project={project}
            employee={selectedEmployeeForUpload}
            onClose={() => {
              setShowEmployeeFileUploadModal(false);
              setSelectedEmployeeForUpload(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default WorkersModal;
